import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { storeTerminalsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Monitor, Plus, Settings } from 'lucide-react';

// Context for selected terminal in online store
const StoreTerminalContext = createContext(null);

export const useStoreTerminal = () => {
  const context = useContext(StoreTerminalContext);
  return context; // May be null if not using terminal selection
};

export const StoreTerminalProvider = ({ children }) => {
  const { user } = useAuth();
  const storeId = user?.entity_id;
  const isCashier = user?.role_name?.toLowerCase().includes('cashier');
  const isManager = user?.role_name?.toLowerCase().includes('manager');

  const [terminals, setTerminals] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const [showSelector, setShowSelector] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTerminalName, setNewTerminalName] = useState('');
  const [newTerminalType, setNewTerminalType] = useState('pos');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (storeId) {
      loadTerminals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const loadTerminals = async () => {
    try {
      if (!loadedOnce.current) setLoading(true); // refreshes must not unmount the page (kills open dialogs)
      let terminalsList = [];
      
      if (isCashier) {
        // Cashiers only see their assigned terminals
        try {
          const assignedRes = await storeTerminalsAPI.getUserAssignedTerminals(user.id);
          terminalsList = assignedRes.data.terminals || [];
        } catch (error) {
          // If endpoint fails, fall back to empty list
          console.error('Failed to load assigned terminals:', error);
          terminalsList = [];
        }
      } else {
        // Managers see all terminals
        const res = await storeTerminalsAPI.list(storeId);
        terminalsList = res.data.terminals || [];
      }
      
      setTerminals(terminalsList);

      // Auto-select saved terminal or first available
      const savedTerminal = localStorage.getItem(`store_terminal_${storeId}`);
      
      if (savedTerminal && terminalsList.find(t => t.terminal_id === savedTerminal)) {
        setSelectedTerminal(savedTerminal);
      } else if (terminalsList.length > 0) {
        // Auto-select first terminal
        setSelectedTerminal(terminalsList[0].terminal_id);
        localStorage.setItem(`store_terminal_${storeId}`, terminalsList[0].terminal_id);
      } else if (isCashier) {
        // Cashier has no assigned terminals - show message
        setSelectedTerminal(null);
      } else {
        // Manager with no terminals - show selector to create one
        setShowSelector(true);
      }
    } catch (error) {
      console.error('Failed to load terminals:', error);
    } finally {
      setLoading(false);
      loadedOnce.current = true;
    }
  };

  const handleSelectTerminal = (terminalId) => {
    setSelectedTerminal(terminalId);
    localStorage.setItem(`store_terminal_${storeId}`, terminalId);
    setShowSelector(false);
  };

  const handleAddTerminal = async () => {
    if (!newTerminalName.trim()) {
      toast.error('Please enter terminal name');
      return;
    }

    try {
      setAdding(true);
      const res = await storeTerminalsAPI.create({
        store_id: storeId,
        terminal_name: newTerminalName,
        terminal_type: newTerminalType
      });

      toast.success(`Terminal ${res.data.terminal.terminal_id} created!`);
      setShowAddModal(false);
      setNewTerminalName('');
      setNewTerminalType('pos');
      await loadTerminals();
      
      // Select the new terminal
      handleSelectTerminal(res.data.terminal.terminal_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create terminal');
    } finally {
      setAdding(false);
    }
  };

  const currentTerminal = terminals.find(t => t.terminal_id === selectedTerminal);

  const value = {
    terminals,
    selectedTerminal,
    currentTerminal,
    setSelectedTerminal: handleSelectTerminal,
    openTerminalSelector: () => setShowSelector(true),
    refreshTerminals: loadTerminals,
    loading
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Loading terminals...</p>
      </div>
    );
  }

  return (
    <StoreTerminalContext.Provider value={value}>
      {children}

      {/* Terminal Selector Modal */}
      <Dialog open={showSelector} onOpenChange={setShowSelector}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              Select Terminal
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {terminals.length === 0 ? (
              <div className="text-center py-8">
                <Monitor className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                {isCashier ? (
                  <>
                    <p className="text-amber-600 font-medium mb-2">No terminals assigned to you</p>
                    <p className="text-sm text-gray-400">Please contact your store manager to assign a terminal</p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-500 mb-2">No terminals configured</p>
                    <p className="text-sm text-gray-400">Create your first terminal to start billing</p>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {terminals.map((terminal) => (
                  <button
                    key={terminal.terminal_id}
                    onClick={() => handleSelectTerminal(terminal.terminal_id)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      selectedTerminal === terminal.terminal_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedTerminal === terminal.terminal_id
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Monitor className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold">{terminal.terminal_id}</p>
                        <p className="text-sm text-gray-500">{terminal.terminal_name}</p>
                      </div>
                    </div>
                    {terminal.has_open_register && (
                      <Badge className="mt-2 bg-green-100 text-green-700">
                        Register Open
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between">
            {!isCashier && (
              <Button
                variant="outline"
                onClick={() => setShowAddModal(true)}
                disabled={terminals.length >= 5}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Terminal
              </Button>
            )}
            {terminals.length > 0 && (
              <Button onClick={() => setShowSelector(false)}>
                Continue
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Terminal Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Terminal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Terminal Name</Label>
              <Input
                value={newTerminalName}
                onChange={(e) => setNewTerminalName(e.target.value)}
                placeholder="e.g., Counter 1, Checkout A"
              />
            </div>
            <div>
              <Label>Terminal Type</Label>
              <Select value={newTerminalType} onValueChange={setNewTerminalType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pos">POS Counter</SelectItem>
                  <SelectItem value="kiosk">Self-Service Kiosk</SelectItem>
                  <SelectItem value="mobile">Mobile Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTerminal} disabled={adding}>
              {adding ? 'Creating...' : 'Create Terminal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StoreTerminalContext.Provider>
  );
};

// Terminal indicator component for header/navbar
export const TerminalIndicator = () => {
  const context = useStoreTerminal();
  
  if (!context) return null;
  
  const { currentTerminal, openTerminalSelector, terminals, loading } = context;

  // Always show the indicator - clickable to open selector
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100">
        <Monitor className="w-4 h-4 text-gray-400 animate-pulse" />
        <span className="text-sm text-gray-500">Loading...</span>
      </div>
    );
  }

  // No terminals assigned/available
  if (!currentTerminal) {
    return (
      <button
        onClick={openTerminalSelector}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors border border-amber-200"
      >
        <Monitor className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-medium text-amber-700">
          {terminals.length === 0 ? 'No Terminal Assigned' : 'Select Terminal'}
        </span>
        <Settings className="w-3 h-3 text-amber-400" />
      </button>
    );
  }

  // Has selected terminal
  return (
    <button
      onClick={openTerminalSelector}
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
    >
      <Monitor className="w-4 h-4 text-blue-600" />
      <span className="text-sm font-medium text-blue-700">{currentTerminal.terminal_id}</span>
      <Settings className="w-3 h-3 text-blue-400" />
    </button>
  );
};

export default StoreTerminalProvider;
